import { useEffect, useMemo, useRef, useState } from "react";
import { client } from "../net/client";
import { useSignal } from "../net/signal";
import { coolProps, useCool } from "../net/pace";
import { avatarSrc } from "../game/avatar";
import { CHAT_TOGGLE, cannedPhrases, cannedText, chatOff, chatSeen, isMuted, muted, setChatOff } from "../game/chat";
import { fa } from "../game/cards";
import type { ChatMessage } from "../gen/bazivan/v1/table_pb";
import { blocked, isBlocked } from "../net/safety";
import { Overlay } from "./Overlay";
import { PlayerSheet } from "./PlayerSheet";

/**
 * چتِ میز — یک جریان برای بازیکن و تماشاچی.
 *
 * دو نما از یک داده: نوارِ گذرا روی میز (که خودش محو می‌شود و جلوی لمسِ
 * کارت را نمی‌گیرد) و برگه‌ی کامل با جعبه‌ی متن. دلیلش این است که بازیکنِ
 * وسطِ نوبت نباید برای دیدنِ یک «آفرین!» چیزی را باز کند، و کسی که می‌خواهد
 * بنویسد نباید با نواری که محو می‌شود سر کند.
 */

/**
 * جریانِ زنده‌ی پیام‌ها — همان کاری که لایوِ اینستاگرام می‌کند.
 *
 * تازه‌ترین پیام پایین می‌نشیند و با آمدنِ بعدی بالا می‌رود و کم‌رنگ‌تر
 * می‌شود، تا ته که کاملاً محو شود. پیامِ کهنه هم بعد از مدتی خودش می‌رود،
 * وگرنه صفحه‌ی ساکت با چند حبابِ ماسیده می‌مانْد.
 */
/** پیام‌هایی که باید دیده شوند: بی‌صداشده‌ها، بلاک‌شده‌ها (net/safety.ts) و حالتِ خاموش این‌جا فیلتر می‌شوند. */
function useVisibleChat(): readonly ChatMessage[] {
  const all = useSignal(client.chat);
  const off = useSignal(chatOff);
  const mutedSet = useSignal(muted); // برای تازه‌شدن سرِ بی‌صدا کردن
  const blockedSet = useSignal(blocked); // و سرِ بلاک
  return useMemo(
    () => (off ? [] : all.filter((m) => !isMuted(m.playerId) && !isBlocked(m.playerId))),
    [all, off, mutedSet, blockedSet],
  );
}

const LIVE_MAX = 6;      // چند پیام هم‌زمان روی صفحه
const LIVE_MS = 14000;   // عمرِ یک پیام روی صفحه

/** پیامِ خودت پررنگ‌تر دیده شود — مثلِ هر چتی. */
function textOfMsg(m: ChatMessage): string {
  return m.canned ? cannedText(m.canned) : m.text;
}

/**
 * tagWatchers: پیامِ تماشاچی نشانِ «تماشاچی» می‌گیرد — سرِ میزِ مافیا هر جمله سند است و حرفِ کسی که کنارِ میز
 * نشسته نباید حرفِ یکی از بازیکن‌ها خوانده شود (همتای MafiaOnlineController.ChatWho ِ اپ).
 */
export function ChatLive({ raised, tagWatchers = false }: { raised: boolean; tagWatchers?: boolean }) {
  const msgs = useVisibleChat();
  const me = useSignal(client.playerId);
  const [now, setNow] = useState(() => Date.now());

  // حبابِ بی‌متن کشیده نمی‌شود: یک مستطیلِ خالی روی میز، خرابیِ بی‌توضیح
  // به نظر می‌رسد.
  const live = msgs
    .filter((m) => now - Number(m.atMs) < LIVE_MS && textOfMsg(m).trim() !== "")
    .slice(-LIVE_MAX);
  // ساعت فقط وقتی می‌زند که چیزی روی صفحه باشد.
  useEffect(() => {
    if (live.length === 0) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [live.length]);

  if (live.length === 0) return null;
  return (
    <div className={"chatlive" + (raised ? " raised" : "")} aria-live="polite">
      {live.map((m, i) => (
        <div
          key={`${m.playerId}-${m.atMs}-${i}`}
          className={"bubble" + (m.playerId === me ? " mine" : "")}
          // فاصله از تازه‌ترین: ۰ یعنی همین الان آمد، هرچه بیشتر کم‌رنگ‌تر.
          style={{ ["--k" as string]: String(live.length - 1 - i) }}
        >
          <b>
            {m.name || (m.seat < 0 ? "تماشاچی" : "بازیکن")}
            {tagWatchers && m.seat < 0 && m.name && <i className="tag">تماشاچی</i>}
          </b>
          <span>{textOfMsg(m)}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * نوارِ چتِ پایینِ صفحه — همیشه آماده، بی این‌که چیزی باز کنی.
 *
 * فقط برای تماشاچی: لبه‌ی پایینِ صفحه‌ی بازیکن دستِ خودش است و یک جعبه‌ی
 * متنِ همیشگی روی ورق‌هایش می‌نشیند. تماشاچی آن‌جا دستی ندارد.
 */
/**
 * closedNote: چت باز است ولی بازی همین حالا ساکتش کرده (شبِ مافیا) — همان را بگو، نه «بسته است»ِ میزبان.
 */
export function ChatBar({ chatOpen, closedNote = "" }: { chatOpen: boolean; closedNote?: string }) {
  const [text, setText] = useState("");
  const [chips, setChips] = useState(false);
  const off = useSignal(chatOff);
  const conn = useSignal(client.state);
  const err = useSignal(client.lastError);

  // دکمه‌ی چتِ سربرگ رفت؛ راهِ برگشت از «خاموش برای من» همین‌جاست.
  if (off) {
    return (
      <div className="chatbar closed">
        چت را برای خودت خاموش کرده‌ای
        <button className="chip" onClick={() => setChatOff(false)}>روشن کن</button>
      </div>
    );
  }
  if (!chatOpen) {
    // «میزبان بسته» فقط برای اتاق راست است؛ میزِ بازیِ سریع یا رباتی میزبان ندارد.
    return <div className="chatbar closed">چت این میز بسته است</div>;
  }
  if (closedNote) return <div className="chatbar closed">{closedNote}</div>;
  // اتصال که نیست، جعبه‌ی متن دروغ می‌گوید: نوشته می‌شود، پاک می‌شود، و هیچ‌جا
  // نمی‌رسد. به‌جایش همان چیزی گفته می‌شود که هست.
  if (conn !== "online") {
    return (
      <div className="chatbar closed">
        {conn === "reconnecting" || conn === "connecting"
          ? "در حال وصل‌شدن…"
          : err || "اتصال قطع است"}
      </div>
    );
  }

  // متن فقط وقتی پاک می‌شود که واقعاً رفته باشد؛ وگرنه دستِ نویسنده می‌مانَد.
  const send = () => { if (client.sendChat(text)) setText(""); };

  return (
    <>
      {chips && (
        <div className="chatchips">
          {cannedPhrases.map((p) => (
            <CannedChip key={p.key} k={p.key} fa={p.fa} onSent={() => setChips(false)} />
          ))}
        </div>
      )}
      <div className="chatbar">
        {/* همه‌ی پیام‌ها و تنظیمِ چت — پیش‌تر دکمه‌ی چتِ سربرگ */}
        <button className="chip hist" onClick={() => window.dispatchEvent(new Event(CHAT_TOGGLE))}
                aria-label="همه‌ی پیام‌ها" title="همه‌ی پیام‌ها"><ChatGlyph /><UnreadBadge /></button>
        <button className={"chip quick" + (chips ? " on" : "")} onClick={() => setChips((v) => !v)}
                aria-label="عبارت‌های آماده" title="عبارت‌های آماده">⚡</button>
        <input
          value={text}
          maxLength={200}
          placeholder="چیزی بنویس…"
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") send(); }}
        />
        <button className="send" onClick={send} disabled={!text.trim()} aria-label="بفرست">➤</button>
      </div>
    </>
  );
}

/** شمارِ پیام‌هایی که از آخرین بارِ بازشدنِ برگه آمده‌اند — نوارِ گذرا محو می‌شود، این عدد می‌مانَد. */
export function useChatUnread(): number {
  const all = useSignal(client.chat).length;
  const seen = useSignal(chatSeen);
  const off = useSignal(chatOff);
  return off ? 0 : Math.max(0, all - seen);
}

/** حبابِ گفت‌وگو — دکمه‌های چتِ پایینِ میز. */
export function ChatGlyph() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 5.5h16a1.5 1.5 0 0 1 1.5 1.5v8a1.5 1.5 0 0 1-1.5 1.5H10l-4.5 3.5V16.5H4A1.5 1.5 0 0 1 2.5 15V7A1.5 1.5 0 0 1 4 5.5z" />
      <circle cx="8" cy="11" r="1.3" /><circle cx="12" cy="11" r="1.3" /><circle cx="16" cy="11" r="1.3" /></svg>
  );
}

/** عددِ نخوانده روی دکمه‌ی چت. */
export function UnreadBadge() {
  const n = useChatUnread();
  return n > 0 ? <i className="badge" aria-label={`${fa(n)} پیام تازه`}>{fa(Math.min(n, 99))}</i> : null;
}

/**
 * دکمه‌ی گردِ چت، پایین-راستِ میزِ ورق — قرینه‌ی میکروفونِ پایین-چپ ([[VoiceButton]]). چت پیش‌تر در
 * سربرگ بود (کاربر، ۲۵ اکتبر ۲۰۲۶: «دکمه‌های سربرگ زیاد است»). منچ و دومینو این را نمی‌گیرند: ردیفِ
 * پایینِ خودشان دکمه‌ی چت و ایموجی دارد (BoardChatButtons).
 *
 * `inline`: تخته‌نرد، دوز و مارپله نوارِ پایینِ تمام‌عرض دارند («تاس بریز»، خطِ وضعیت) و مافیا تابلوی
 * پایین — دکمه‌ی شناور رویشان می‌افتاد؛ آن‌جا دکمه تکه‌ای از همان نوار است (مافیا: کنارِ ایموجیِ گوشه‌ی حلقه).
 */
export function ChatFab({ inline = false }: { inline?: boolean }) {
  return (
    <button className={"chatfab" + (inline ? " inline" : "")} onClick={() => window.dispatchEvent(new Event(CHAT_TOGGLE))} aria-label="چت میز" title="چت میز">
      <ChatGlyph />
      <UnreadBadge />
    </button>
  );
}

/** برگه‌ی کامل: تاریخچه، عبارت‌های آماده، جعبه‌ی متن و تنظیم‌ها. */
export function ChatPanel({ open, onClose, chatOpen, host }:
  { open: boolean; onClose: () => void; chatOpen: boolean; host: boolean }) {
  const msgs = useVisibleChat();
  const all = useSignal(client.chat).length;
  const off = useSignal(chatOff);
  // برگه باز است (یا چت خاموش): همه دیده شده. پیام‌های پاک‌شده (میزِ تازه) شمارنده را پایین می‌آورند.
  useEffect(() => {
    if (open || off || chatSeen.value > all) chatSeen.value = all;
  }, [open, off, all]);
  const [text, setText] = useState("");
  const [who, setWho] = useState<ChatMessage | null>(null);
  const listEnd = useRef<HTMLDivElement>(null);

  // با هر پیامِ تازه به ته می‌چسبد — همان کاری که هر چتی می‌کند.
  useEffect(() => { if (open) listEnd.current?.scrollIntoView({ block: "end" }); }, [msgs.length, open]);

  if (!open) return null;

  const send = () => { if (client.sendChat(text)) setText(""); };

  return (
    // همان Overlay ِ بقیه‌ی پنجره‌ها: role=dialog، تمرکزِ آغازین و حبسِ تمرکز، Esc،
    // لمسِ بیرون و «بازگشت»ِ گوشی. پیش‌تر یک div بود که فقط Esc را می‌فهمید و
    // Tab از رویش به میزِ زیرِ پرده می‌رفت.
    <Overlay label="چت میز" className="chatwrap" onClose={onClose}>
    <div className="chatsheet">
      <div className="chathead">
        <b>چت میز</b>
        <div className="spacer" />
        {host && (
          <button className="chip" onClick={() => client.setChatOpen(!chatOpen)}>
            {chatOpen ? "بستن چت برای همه" : "بازکردن چت"}
          </button>
        )}
        <button className="chip" onClick={() => setChatOff(!off)}>
          {off ? "روشن‌کردن چت" : "خاموش برای من"}
        </button>
        <button className="chip" onClick={onClose} aria-label="بستن">✕</button>
      </div>

      <div className="chatlist">
        {off ? (
          <div className="muted" style={{ textAlign: "center", padding: 20 }}>
            چت را برای خودت خاموش کرده‌ای. کسی خبر ندارد.
          </div>
        ) : msgs.length === 0 ? (
          <div className="muted" style={{ textAlign: "center", padding: 20 }}>
            هنوز کسی چیزی نگفته.
          </div>
        ) : (
          msgs.map((m, i) => <Row key={`${m.playerId}-${m.atMs}-${i}`} m={m} onPick={setWho} />)
        )}
        <div ref={listEnd} />
      </div>

      {chatOpen ? (
        <>
          <div className="chatcanned">
            {cannedPhrases.map((p) => <CannedChip key={p.key} k={p.key} fa={p.fa} />)}
          </div>
          <div className="row chatbox">
            <input
              value={text}
              maxLength={200}
              placeholder="چیزی بنویس…"
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") send(); }}
            />
            <button className="btn" style={{ width: "auto", paddingInline: 18 }} onClick={send} disabled={!text.trim()}>
              بفرست
            </button>
          </div>
        </>
      ) : (
        <div className="muted" style={{ textAlign: "center", padding: 12 }}>
          چت این میز بسته است.
        </div>
      )}
      {who && (
        <PlayerSheet
          player={{ id: who.playerId, name: who.name, avatar: who.avatar, spectator: who.seat < 0 }}
          context={who.seat < 0 ? "watch" : "chat"}
          onClose={() => setWho(null)}
        />
      )}
    </div>
    </Overlay>
  );
}

/**
 * عبارتِ آماده. پس از لمس کم‌رنگ می‌شود تا وقتی سرعت‌گیر دوباره راهش بدهد — همان عبارت چند ثانیه،
 * بقیه فقط وقتی سهمیه‌ی این چند ثانیه تمام شده (net/pace.ts). ده لمسِ «آفرین» یک «آفرین» است.
 */
function CannedChip({ k, fa, onSent }: { k: string; fa: string; onSent?: () => void }) {
  const until = Math.max(useCool(client.chatPace, "c:" + k), useCool(client.cannedPace));
  const cool = coolProps(until);
  return (
    <button className={"chip" + cool.className} style={cool.style} aria-disabled={until ? true : undefined}
            onClick={() => { if (client.sendCanned(k)) onSent?.(); }}>
      {fa}
    </button>
  );
}

/** یک پیام. لمسِ نام برگه‌ی همان بازیکن را باز می‌کند: بی‌صدا، بلاک، گزارش. */
function Row({ m, onPick }: { m: ChatMessage; onPick: (m: ChatMessage) => void }) {
  const me = useSignal(client.playerId);
  const mine = m.playerId === me;
  return (
    <div className={`chatrow ${mine ? "mine" : ""}`}>
      <img className="seatface" src={avatarSrc(m.avatar)} alt="" width={26} height={26} />
      <div className="body">
        <button
          className="who"
          disabled={mine}
          title={mine ? undefined : "بی‌صدا، بلاک یا گزارش"}
          onClick={() => { if (!mine) onPick(m); }}
        >
          {m.name || "بازیکن"}
          {/* تماشاچی صندلی ندارد؛ همین یک نشان کافی است تا معلوم شود از کنارِ میز حرف می‌زند */}
          {m.seat < 0 && <span className="tag">تماشاچی</span>}
        </button>
        <span className={m.canned ? "canned" : ""}>{textOfMsg(m)}</span>
      </div>
    </div>
  );
}
